import { useMemo, useRef } from "react"
import { CARD_KEYS, CARD_MAP, DETAIL_DATA } from "../data"
import Highcharts from 'highcharts'
import { useHighcharts } from "../../../hooks/useHighcharts"
import { useTranslation } from "react-i18next"
import HighchartsReact from "highcharts-react-official"
import { COLOR } from "../../../data/variables"
import { useChart } from "../../../hooks/useCharts"
import InfoDisplay from "../../../components/InfoDisplay"
import Table from "../../../components/Table"
import { DashboardData } from "."

export const useCardDetails = (card: CARD_KEYS, data: DashboardData) => {
  const { t } = useTranslation()
  const { calculateTrend } = useChart()

  const chartRef = useRef()

  const series = useMemo(() => {
    const { hasGraph, graphDays, key } = DETAIL_DATA[card] ?? {}
    if (!hasGraph) return []
    // data.daily can be longer than needed but is sorted by date
    // we use the last amount of days we need
    const dataArr = data.daily.slice(-(graphDays ?? 0))
    const serie = dataArr.map((day) => [day.day, day[key]])
    const trend = calculateTrend(serie)
    return [{
      name: t('Data'),
      color: COLOR.BLUE_70,
      data: serie
    }, {
      name: t('Trend'),
      color: COLOR.RED_70,
      data: trend
    }]
  }, [data, card])

  const { options } = useHighcharts({
    title: '',
    enableHorizontalScroll: false,
    axis: {
      xType: 'datetime',
      yAxisTitle: t('Value'),
      xFormat: '{value:%d.%m}'
    },
    tooltip: CARD_MAP[card]?.unit ?? '',
    series,
  })
  
  const graph = useMemo(() => {
    const { hasGraph } = DETAIL_DATA[card] ?? {}
    if (!hasGraph) return null

    return (
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
      />
    )
  }, [card, options])

  const table = useMemo(() => {
    const { hasTable, path, columns, showRow } = DETAIL_DATA[card] ?? {}
    if (!hasTable) return null
    const mapData = data[path] ?? Object.values(data)

    const header = columns?.reduce((obj, col) => {
      const { label, id, formatFunction } = col
      obj[id] = {
        label,
      }
      if (formatFunction) obj[id].formatFunction = formatFunction
      return obj
    }, {})

    const rows = mapData?.reduce((arr: any[], row) => {
      if (typeof showRow !== 'undefined' && !showRow(row)) return arr
      const newRow = {}
      columns?.forEach((col) => {
        const { id, key } = col
        newRow[id] = row?.[key] ?? ''
      })
      arr.push(newRow)
      return arr
    }, [])

    return (
      <Table
        tableHeader={header}
        headerRowHeight={35}
        rowHeight={35}
        rows={rows}
        showSearch={false}
        includePageFooter={false}
      />
    )
  }, [card])

  const info = useMemo(() => {
    const { info } = DETAIL_DATA[card] ?? {}
    if (!info) return null

    const { key: dataKey, fields } = info
    return (
      <InfoDisplay
        data={fields.map((d) => {
          const { label, key, type } = d;
          return {
            label,
            value: type === 'map'
              ? { lon: data?.[dataKey]?.poslon, lat: data?.[dataKey]?.poslat, heading: data?.[dataKey]?.heading }
              : data?.[dataKey]?.[key],
            type
          }
        })}
      />
    )
  }, [card, data])

  return {
    graph,
    table,
    info
  }
}
